import { t } from '@trimble/i18n';
import { Accessor, Component, createMemo, Show } from 'solid-js';
import { BtnClasses, Chips, IconButton, PanelLayout } from '@tcweb/connect-ui';
import {
  AssigneeResponse,
  ViewDetailsResponse,
  TcTcpsApiService,
  TcRegion,
  TcObjectType,
} from '@data-access/connect/tc-tcps';
import { UserComments, UserComment } from '../../../comments/UserComments';
import { ThumbnailLoader } from '../../../thumbnail-loader/ThumbnailLoader';
import { Tags } from '../../../tags/Tags';
import { WithLabel } from '@tcweb/connect-ui';
import { getFormattedDate } from '@trimble/utilities';

export interface ViewDetailsTabProps {
  view: ViewDetailsResponse;
  tcTcpsApi: TcTcpsApiService;
  region: Accessor<TcRegion | undefined>;
  onClose(): void;
}

export const ViewDetailsTab: Component<ViewDetailsTabProps> = (props) => {
  const commentsQuery = createMemo(() => {
    const r = props.region();
    if (!r) return undefined;

    return props.tcTcpsApi.listAllCommentsQuery(
      r,
      props.view.id,
      TcObjectType.VIEW
    );
  });

  const comments = createMemo((): UserComment[] => {
    const query = commentsQuery();
    if (!query || !query.isSuccess) return [];

    return query.data.map((c) => ({
      ...c,
      createdBy: `${c.createdBy.firstName} ${c.createdBy.lastName}`,
      modifiedBy: `${c.modifiedBy.firstName} ${c.modifiedBy.lastName}`,
    }));
  });

  const tagsQuery = createMemo(() => {
    const r = props.region();
    if (!r) return undefined;

    return props.tcTcpsApi.listTagsQuery(
      r,
      props.view.projectId,
      props.view.id,
      TcObjectType.VIEW
    );
  });

  const tags = createMemo((): string[] => {
    const query = tagsQuery();
    if (!query || !query.isSuccess) return [];

    return query.data.map((tagItem) => tagItem.label);
  });

  const assignees = createMemo(
    (): AssigneeResponse[] => props.view.assignees || []
  );

  const sharedWithLabel = createMemo((): string => {
    const label = t('mobileMapping.views.viewDetails.sharedWith');
    const shares = assignees();

    if (shares.length === 0) return label;

    return `${label} (${shares.length.toString()})`;
  });

  return (
    <div data-testid="view-details-tab" class="contents">
      <PanelLayout
        panelTitle={props.view.name}
        rightActions={
          <IconButton
            color={BtnClasses.TERTIARY}
            icon="tc-icon-content-close"
            size="icon-medium"
            title={t('general.close')}
            data-cy="view-details-tab-close"
            clicked={() => {
              props.onClose();
            }}
          />
        }
        content={
          <>
            <div class="w-full h-32 border-b-2 border-[#d0d0d7]">
              <ThumbnailLoader
                thumbnailUrl={props.view.thumbnail}
                altText={props.view.name}
                class="bg-gray-50"
                imageClass="w-full h-full rounded-t"
                getThumbnailBlob={(url) => props.tcTcpsApi.getImageBlobUrl(url)}
              />
            </div>

            <div class="flex flex-col p-3">
              <div class="h5">{t('mobileMapping.views.viewDetails.title')}</div>

              <WithLabel
                label={t('mobileMapping.views.viewDetails.description')}
                name=""
              >
                {props.view.description}
              </WithLabel>

              <WithLabel
                label={t('mobileMapping.views.viewDetails.createdOn')}
                name=""
              >
                {getFormattedDate(
                  props.view.createdOn,
                  props.view.createdBy.firstName,
                  props.view.createdBy.lastName
                )}
              </WithLabel>

              <WithLabel
                label={t('mobileMapping.views.viewDetails.modifiedOn')}
                name=""
              >
                {getFormattedDate(
                  props.view.modifiedOn,
                  props.view.modifiedBy.firstName,
                  props.view.modifiedBy.lastName
                )}
              </WithLabel>

              <WithLabel label={sharedWithLabel()} name="">
                <Show
                  when={assignees().length > 0}
                  fallback={t('mobileMapping.views.viewDetails.noUsers')}
                >
                  <div class="py-1 -my-1">
                    <Chips
                      chips={assignees().map(
                        (i) => `${i.firstName} ${i.lastName}`
                      )}
                    />
                  </div>
                </Show>
              </WithLabel>
            </div>

            <Show when={tags().length > 0}>
              <div class="border-t border-[#d0d0d7]">
                <Tags tags={tags()} />
              </div>
            </Show>

            <Show when={comments().length > 0}>
              <div class="border-t border-[#d0d0d7]">
                <UserComments comments={comments()} />
              </div>
            </Show>
          </>
        }
      />
    </div>
  );
};
